import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7000';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore session from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }

    setIsLoading(false);
  }, []);

  const login = async (email, password) => {
    if (!email || !password) {
      throw new Error('Username and password are required');
    }

    const res = await fetch(`${API_URL}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      throw new Error('Server error. Please try again.');
    }

    const data = await res.json();

    if (!data.success) {
      throw new Error(data.message || 'Invalid credentials. Access denied.');
    }

    const userWithAvatar = { ...data.user, avatar: '👨‍⚕️' };

    localStorage.setItem('authToken', data.token);
    localStorage.setItem('user', JSON.stringify(userWithAvatar));
    setUser(userWithAvatar);

    return userWithAvatar;
  };

  const register = async (email, name, password) => {
    if (!email || !name || !password) {
      throw new Error('All fields are required');
    }

    const res = await fetch(`${API_URL}/api/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name, password }),
    });

    if (!res.ok) {
      throw new Error('Server error. Please try again.');
    }

    const data = await res.json();

    if (!data.success) {
      throw new Error(data.message || 'Registration failed.');
    }

    const userWithAvatar = { ...data.user, avatar: '👨‍⚕️' };

    localStorage.setItem('authToken', data.token);
    localStorage.setItem('user', JSON.stringify(userWithAvatar));
    setUser(userWithAvatar);

    return userWithAvatar;
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  const value = {
    user,
    isLoading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
